'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import React from 'react';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getBlogExcerpt } from '@eliadmin/core/src/utils/blog-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { GoArrowUpRight } from 'react-icons/go';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export type BlogCardProps = {
  className?: {
    main?: string;
    title?: string;
    description?: string;
  };
  blog: Blog;
  firstBlog?: boolean;
  headingTag?: 'h2' | 'h3';
};

export function BlogCard({
  blog,
  className,
  firstBlog = false,
  headingTag: Title = 'h3',
}: BlogCardProps) {
  const textContent = getBlogExcerpt(blog.content);
  const locale = useLocaleStore(state => state.currentLocale);

  let formattedDate;

  try {
    formattedDate = new Date(blog.created_at).toLocaleDateString(locale);
  } catch (error) {
    if (error instanceof RangeError) {
      formattedDate = new Date(blog.created_at).toLocaleDateString('en-US');
    } else {
      throw error;
    }
  }

  const { t } = useTranslations();

  return (
    <>
      {firstBlog ? (
        <Link
          prefetch={false}
          href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
          className="col-span-1 flex w-full flex-col items-start justify-start gap-4 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between lg:col-span-3 lg:gap-10 xl:gap-12"
        >
          <Image
            src={blog.image.url}
            alt={resolveImageAlt(blog?.image, blog?.name)}
            width={0}
            height={0}
            className="aspect-square max-h-[545px] w-full object-cover object-center md:max-w-[713px]"
            sizes="(max-width: 768px) 100vw, 40vw"
          />
          <div className="flex w-full flex-col items-start justify-start gap-4 md:max-w-[477px]">
            <div className="flex h-8 w-full items-center justify-between">
              {blog.categories.length > 0 && (
                <span className="bg-primary/10 px-2 py-1 font-medium tracking-tight text-black text-primary">
                  {blog.categories[0]?.name}
                </span>
              )}
              <p className="text-sm font-medium text-black/70">{formattedDate}</p>
            </div>
            <div className="flex w-full flex-col items-start justify-start gap-2">
              <Title
                data-ea-visual-tag={Title === 'h3' ? undefined : 'h3'}
                className={cn(
                  'line-clamp-3 text-lg font-normal tracking-tighter sm:text-2xl lg:text-3xl xl:text-4xl',
                )}
              >
                {blog.name}
              </Title>
              <p
                className={cn(
                  'line-clamp-2 w-full text-black/70',
                  'text-sm font-light sm:text-base',
                )}
              >
                {textContent}
              </p>
              <span className="flex h-12 items-center justify-center gap-2 border-b border-b-black/15 text-sm font-medium tracking-tighter text-primary transition-all hover:bg-primary/5 sm:h-14 sm:text-base">
                {t('text1148')} <GoArrowUpRight size={18} />
              </span>
            </div>
          </div>
        </Link>
      ) : (
        <Link
          prefetch={false}
          href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
          className={cn(
            'col-span-1 flex w-full flex-col items-start justify-start gap-4 sm:gap-5 md:gap-6',
            className?.main,
          )}
        >
          <Image
            src={blog?.image?.url ?? '/images/placeholder.png'}
            alt={resolveImageAlt(blog?.image, blog?.name)}
            width={0}
            height={0}
            className="h-[230px] w-full object-cover object-center sm:h-[250px] xl:h-[300px]"
            sizes="(max-width: 640px) 300px, 390px"
          />
          <div className="flex flex-col items-start justify-start gap-4">
            <div className="flex h-8 w-full items-center justify-between">
              {blog.categories.length > 0 && (
                <span className="bg-primary/10 px-2 py-1 font-medium tracking-tight text-black text-primary">
                  {blog.categories[0]?.name}
                </span>
              )}
              <p className="text-sm font-medium text-black/70">{formattedDate}</p>
            </div>
            <div className="flex w-full flex-col items-start justify-start gap-2">
              <Title
                data-ea-visual-tag={Title === 'h3' ? undefined : 'h3'}
                className={cn(
                  'line-clamp-2 h-14 text-lg font-normal tracking-tighter sm:text-xl xl:h-16 xl:text-2xl',
                  className?.title,
                )}
              >
                {blog.name}
              </Title>
              <p
                className={cn(
                  'line-clamp-2 w-full text-black/70',
                  'text-sm font-light sm:text-base',
                  className?.description,
                )}
              >
                {textContent}
              </p>
            </div>
          </div>
          <span className="flex h-12 items-center justify-center gap-2 border-b border-b-black/15 text-sm font-medium tracking-tighter text-primary transition-all hover:bg-primary/5 sm:h-14 sm:text-base">
            {t('text1148')} <GoArrowUpRight size={18} />
          </span>
        </Link>
      )}
    </>
  );
}
